import React from 'react'
import { Head } from '@inertiajs/react'
import { PublicLayout } from '@/components/frontend/templates/PublicLayout'
import { Section } from '@/components/frontend/atoms/layout/Section'
import { Container } from '@/components/frontend/atoms/layout/Container'
import { Stack } from '@/components/shared/atoms/layout/Stack'
import { Inline } from '@/components/shared/atoms/layout/Inline'
import { Divider } from '@/components/shared/atoms/layout/Divider'
import { PostHeader } from '@/components/frontend/molecules/PostHeader'
import { AuthorCTA } from '@/components/frontend/molecules/AuthorCTA'
import { Prose } from '@/components/frontend/atoms/typography/Prose'
import { CoverImage } from '@/components/frontend/atoms/media/CoverImage'
import { TagBadge } from '@/components/frontend/atoms/content/TagBadge'
import { ButtonLink } from '@/components/frontend/atoms/navigation/ButtonLink'
import { RelatedPosts } from '@/components/frontend/organisms/RelatedPosts'
import { useTranslation } from '@/hooks/useTranslation'
import type { PageProps } from '@/types'
import type { PublicPost } from '@/types/frontend'

type Props = PageProps<{
  post: PublicPost
  relatedPosts: PublicPost[]
}>

const BlogShow: React.FC<Props> = ({ post, relatedPosts }) => {
  const { t, lp } = useTranslation()
  const title = (post.meta_title || post.title) + ' - CodeOfMario'

  return (
    <PublicLayout>
      <Head title={title} />
      <Section padding="md">
        <Container maxWidth="md">
          <Stack gap="8">
            <Inline>
              <ButtonLink href={lp('/blog')} variant="ghost" size="sm">&larr; {t('blog.back_to_blog')}</ButtonLink>
            </Inline>
            <PostHeader post={post} />
            {post.cover_image_url && (
              <CoverImage src={post.cover_image_url} alt={post.title} aspectRatio="2/1" framed />
            )}
            {post.content && <Prose html={post.content} />}
            {post.tags.length > 0 && (
              <Inline gap="2" wrap>
                {post.tags.map((tag) => (
                  <TagBadge key={tag.slug} name={tag.name} slug={tag.slug} basePath={lp('/blog')} />
                ))}
              </Inline>
            )}
            <Divider />
            <AuthorCTA />
          </Stack>
        </Container>
      </Section>
      <RelatedPosts posts={relatedPosts} />
    </PublicLayout>
  )
}

export default BlogShow
